import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  BookOpen, 
  Search, 
  Play, 
  ChevronRight, 
  Rocket, 
  Layout, 
  Image, 
  Plug, 
  CreditCard, 
  Settings,
  Filter,
  Loader2,
  ExternalLink,
  Clock,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';
import MainLayout from '@/components/layout/MainLayout';
import ReactMarkdown from 'react-markdown';

const CATEGORY_ICONS = {
  getting_started: Rocket,
  funnels: Filter,
  landings: Layout,
  creatives: Image,
  integrations: Plug,
  billing: CreditCard,
  advanced: Settings
};

const Tutorials = () => {
  const { api } = useAuth();
  const [tutorials, setTutorials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedTutorial, setSelectedTutorial] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [tutorialsRes, categoriesRes] = await Promise.all([
        api.get('/tutorials'),
        api.get('/tutorials/categories')
      ]);
      setTutorials(tutorialsRes.data.tutorials || []);
      setCategories(categoriesRes.data.categories || []);
    } catch (error) {
      toast.error('Error al cargar tutoriales');
    } finally {
      setLoading(false);
    }
  };

  const filteredTutorials = tutorials.filter(t => {
    const matchesCategory = !selectedCategory || t.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const CategoryIcon = ({ category }) => {
    const Icon = CATEGORY_ICONS[category] || BookOpen;
    return <Icon className="w-5 h-5" />;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex-1 p-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 text-brand animate-spin" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <main className="flex-1 p-8 overflow-auto">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-text-main flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-brand" />
              Centro de Ayuda
            </h1>
            <p className="text-text-muted mt-2">
              Aprende a sacar el máximo partido a FASCINAI ONE
            </p>
          </div>

          {/* Search */}
          <div className="relative mb-8 max-w-xl">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <Input
              placeholder="Buscar tutoriales..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-surface border-border-subtle text-text-main"
              data-testid="tutorials-search"
            />
          </div>

          {/* Show tutorial detail or list */}
          {selectedTutorial ? (
            <div className="animate-fade-in">
              {/* Back button */}
              <Button
                variant="ghost"
                onClick={() => setSelectedTutorial(null)}
                className="mb-6 text-text-muted hover:text-text-main"
              >
                <ChevronRight className="w-4 h-4 rotate-180 mr-2" />
                Volver a tutoriales
              </Button>

              {/* Tutorial content */}
              <div className="bg-surface rounded-2xl border border-border-subtle p-8">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand/10 text-brand text-sm mb-3">
                      <CategoryIcon category={selectedTutorial.category} />
                      {categories.find(c => c.id === selectedTutorial.category)?.name}
                    </span>
                    <h2 className="text-2xl font-bold text-text-main">
                      {selectedTutorial.title}
                    </h2>
                    <p className="text-text-muted mt-2">{selectedTutorial.description}</p>
                  </div>
                  {selectedTutorial.views && (
                    <span className="flex items-center gap-1 text-text-muted text-sm">
                      <Eye className="w-4 h-4" />
                      {selectedTutorial.views} vistas
                    </span>
                  )}
                </div>

                {/* Video embed */}
                {selectedTutorial.video_url && (
                  <div className="mb-8 aspect-video rounded-xl overflow-hidden bg-black">
                    <iframe
                      src={selectedTutorial.video_url}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                )}

                {/* Markdown content */}
                <div className="prose prose-invert max-w-none">
                  <ReactMarkdown>{selectedTutorial.content}</ReactMarkdown>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Categories Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-8">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className={`p-4 rounded-xl border transition-all text-center ${
                    !selectedCategory 
                      ? 'bg-brand/10 border-brand text-brand' 
                      : 'bg-surface border-border-subtle text-text-muted hover:border-brand/50'
                  }`}
                >
                  <BookOpen className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">Todos</span>
                  <span className="block text-xs mt-1 opacity-60">{tutorials.length}</span>
                </button>
                
                {categories.map((cat) => {
                  const Icon = CATEGORY_ICONS[cat.id] || BookOpen;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`p-4 rounded-xl border transition-all text-center ${
                        selectedCategory === cat.id 
                          ? 'bg-brand/10 border-brand text-brand' 
                          : 'bg-surface border-border-subtle text-text-muted hover:border-brand/50'
                      }`}
                    >
                      <Icon className="w-6 h-6 mx-auto mb-2" />
                      <span className="text-sm font-medium">{cat.name}</span>
                      <span className="block text-xs mt-1 opacity-60">{cat.count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Tutorials List */}
              {filteredTutorials.length === 0 ? (
                <div className="text-center py-16">
                  <BookOpen className="w-16 h-16 text-text-muted mx-auto mb-4 opacity-50" />
                  <h3 className="text-xl font-semibold text-text-main mb-2">
                    No hay tutoriales
                  </h3>
                  <p className="text-text-muted">
                    {searchQuery 
                      ? 'No se encontraron tutoriales con ese término' 
                      : 'Pronto añadiremos contenido a esta categoría'}
                  </p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredTutorials.map((tutorial) => (
                    <button
                      key={tutorial.id}
                      onClick={() => setSelectedTutorial(tutorial)}
                      className="group bg-surface rounded-xl border border-border-subtle p-6 text-left transition-all hover:border-brand/50 hover:shadow-lg"
                      data-testid={`tutorial-card-${tutorial.id}`}
                    >
                      {/* Category badge */}
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-brand/10 text-brand text-xs mb-3">
                        <CategoryIcon category={tutorial.category} />
                        {categories.find(c => c.id === tutorial.category)?.name}
                      </span>

                      {/* Title & Description */}
                      <h3 className="text-lg font-semibold text-text-main mb-2 group-hover:text-brand transition-colors">
                        {tutorial.title}
                      </h3>
                      <p className="text-text-muted text-sm line-clamp-2 mb-4">
                        {tutorial.description}
                      </p>

                      {/* Footer */}
                      <div className="flex items-center justify-between pt-4 border-t border-border-subtle">
                        {tutorial.video_url ? (
                          <span className="flex items-center gap-1.5 text-xs text-brand">
                            <Play className="w-4 h-4" />
                            Video incluido
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 text-xs text-text-muted">
                            <Clock className="w-4 h-4" />
                            5 min lectura
                          </span>
                        )}
                        <ChevronRight className="w-5 h-5 text-text-muted group-hover:text-brand group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </MainLayout>
  );
};

export default Tutorials;
